import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Box, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { slugify } from "../common/cookbook";

const nodeText = (node) =>
  node.type === "text" ? node.value : (node.children || []).map(nodeText).join("");

// "## Section" headings get an id (slug of their text) so a table of contents can link to them.
const components = {
  h2: ({ node, children, ...props }) => (
    <h2 id={slugify(nodeText(node))} {...props}>
      {children}
    </h2>
  ),
  table: ({ node, ...props }) => (
    <div className="md-table">
      <table {...props} />
    </div>
  ),
  a: ({ node, href, children, ...props }) =>
    /^https?:/i.test(href || "") ? (
      <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
        {children}
      </a>
    ) : (
      <a href={href} {...props}>
        {children}
      </a>
    ),
};

// Renders GitHub-flavoured Markdown (tables, task lists, strikethrough) styled to the site theme.
// Raw HTML in the source is not rendered.
const MarkdownView = ({ children }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const accent = theme.palette.secondary.main;
  const ink = theme.palette.text.primary;

  return (
    <Box
      sx={{
        color: theme.palette.text.primary,
        fontSize: "0.95rem",
        lineHeight: 1.7,
        overflowWrap: "break-word",
        "& > :first-of-type": { mt: 0 },
        "& h1, & h2, & h3, & h4, & h5": { lineHeight: 1.3, scrollMarginTop: "1rem" },
        "& h1": { fontSize: "1.75rem", fontWeight: 700, mt: "1.5rem", mb: "0.75rem" },
        "& h2": {
          fontSize: "1.35rem",
          fontWeight: 700,
          mt: "2.25rem",
          mb: "0.75rem",
          pb: "0.4rem",
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
        "& h3": { fontSize: "1.1rem", fontWeight: 700, mt: "1.6rem", mb: "0.5rem" },
        "& h4, & h5": { fontSize: "1rem", fontWeight: 600, mt: "1.25rem", mb: "0.4rem" },
        "& p": { my: "0.6rem" },
        "& ul, & ol": { pl: "1.5rem", my: "0.5rem" },
        "& li": { my: "0.2rem" },
        "& li > ul, & li > ol": { my: "0.2rem" },
        "& .contains-task-list": { listStyle: "none", pl: "0.5rem" },
        "& input[type=checkbox]": { mr: "0.5rem" },
        "& strong": { fontWeight: 700 },
        "& a": { color: accent },
        "& blockquote": {
          m: "1rem 0",
          p: "0.75rem 1rem",
          borderLeft: `4px solid ${accent}`,
          borderRadius: "0 10px 10px 0",
          backgroundColor: alpha(accent, isDark ? 0.12 : 0.08),
          "& p": { my: "0.25rem" },
        },
        "& code": {
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
          fontSize: "0.85em",
          px: "0.35em",
          py: "0.1em",
          borderRadius: "5px",
          backgroundColor: alpha(ink, isDark ? 0.1 : 0.06),
        },
        "& pre": {
          p: "0.9rem 1rem",
          overflowX: "auto",
          borderRadius: "10px",
          backgroundColor: alpha(ink, isDark ? 0.08 : 0.04),
          "& code": { p: 0, backgroundColor: "transparent" },
        },
        "& .md-table": { overflowX: "auto", my: "1rem" },
        "& table": { borderCollapse: "collapse", width: "100%", fontSize: "0.875rem", lineHeight: 1.5 },
        "& th, & td": {
          border: `1px solid ${theme.palette.divider}`,
          p: "0.5rem 0.75rem",
          textAlign: "left",
          verticalAlign: "top",
        },
        "& th": { fontWeight: 700, backgroundColor: alpha(accent, isDark ? 0.14 : 0.1) },
        "& tbody tr:nth-of-type(even)": { backgroundColor: alpha(ink, isDark ? 0.04 : 0.025) },
        "& hr": { border: 0, borderTop: `1px solid ${theme.palette.divider}`, my: "1.5rem" },
        "& img": { maxWidth: "100%" },
      }}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {children}
      </ReactMarkdown>
    </Box>
  );
};

export default MarkdownView;
